import { useState } from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { X } from 'lucide-react';

import type { CohortCell, CohortRow, ProductCohortTable } from '@/types/admin';

export type HeatmapMetric = 'retention' | 'cancellation';

type SelectedCell =
  | { kind: 'row'; rowIndex: number; cellIndex: number }
  | { kind: 'weighted'; cellIndex: number }
  | null;

/**
 * Heatmap rendering of one product's weekly-cohort × N-bucket grid.
 *
 * Two metric modes share the component:
 * - "retention" (default): M_N columns, white→ink gradient, cells report
 *   % of cohort still retained at month N (higher = better, darker).
 * - "cancellation": W_N columns, white→red gradient, cells report % of
 *   cohort that clicked cancel in week N (higher = worse, redder).
 *
 * Cells are clickable — selecting one opens a detail panel above the table
 * with two drill-down buttons ("View full cohort" + "View retained/cancelled")
 * that link into the Subscriptions screen pre-filtered.
 *
 * Partial cells (window started for earliest sub, not yet for latest) render
 * with an asterisk and a dashed border — value is a real lower bound.
 * Unobservable cells render muted "—".
 */
export function CohortHeatmap({
  table,
  detailHref,
  metric = 'retention',
}: {
  table: ProductCohortTable;
  detailHref?: string;
  metric?: HeatmapMetric;
}) {
  const cap = table.rows[0]?.cells.length ?? 0;
  const title = table.product_label.trim();
  const subtitles = buildSubtitles(table);
  const colLabel = metric === 'cancellation' ? 'W' : 'M';

  const [selected, setSelected] = useState<SelectedCell>(null);

  const selectedCell: CohortCell | null = (() => {
    if (!selected) return null;
    if (selected.kind === 'row') {
      return table.rows[selected.rowIndex]?.cells[selected.cellIndex] ?? null;
    }
    return table.weighted[selected.cellIndex] ?? null;
  })();
  const selectedRow: CohortRow | null =
    selected?.kind === 'row' ? table.rows[selected.rowIndex] ?? null : null;

  const toggleCell = (next: SelectedCell) => {
    setSelected((curr) => {
      if (!curr || !next) return next;
      if (curr.kind !== next.kind) return next;
      if (
        curr.kind === 'row' &&
        next.kind === 'row' &&
        curr.rowIndex === next.rowIndex &&
        curr.cellIndex === next.cellIndex
      )
        return null;
      if (
        curr.kind === 'weighted' &&
        next.kind === 'weighted' &&
        curr.cellIndex === next.cellIndex
      )
        return null;
      return next;
    });
  };

  return (
    <div className="border border-line rounded-lg bg-raised overflow-hidden">
      <div className="p-16 border-b border-line flex flex-col gap-6">
        <div className="flex flex-wrap items-baseline gap-x-16 gap-y-4">
          {detailHref ? (
            <Link
              to={detailHref}
              className="type-heading-md underline decoration-line underline-offset-4 hover:decoration-ink"
            >
              {title}
            </Link>
          ) : (
            <h3 className="type-heading-md">{title}</h3>
          )}
          <span className="type-label-sm text-ink-muted">
            {table.n_total.toLocaleString()} subs · {table.cycle_duration_days}d cycle
          </span>
          <code className="type-label-sm text-ink-muted ml-auto">{table.product_id}</code>
        </div>
        {subtitles.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-12 gap-y-2 type-label-sm text-ink-muted">
            {subtitles.map((s, i) => (
              <span key={i}>{s}</span>
            ))}
          </div>
        )}
      </div>

      {selected && selectedCell ? (
        <CellDetailPanel
          table={table}
          row={selectedRow}
          cell={selectedCell}
          metric={metric}
          onClose={() => setSelected(null)}
        />
      ) : null}

      <div className="overflow-x-auto">
        <table className="w-full type-label-sm">
          <thead>
            <tr className="text-left">
              <Th>Cohort (week)</Th>
              <Th align="right">N</Th>
              {Array.from({ length: cap }).map((_, i) => (
                <Th key={i} align="center">
                  {colLabel}
                  {i}
                </Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, rIdx) => (
              <tr key={row.cohort_start} className="border-t border-line-subtle">
                <Td>
                  {format(parseISO(row.cohort_start), 'MMM d')} →{' '}
                  {format(parseISO(row.cohort_end), 'MMM d')}
                </Td>
                <Td align="right">{row.n}</Td>
                {row.cells.map((cell, cIdx) => {
                  const isActive =
                    selected?.kind === 'row' &&
                    selected.rowIndex === rIdx &&
                    selected.cellIndex === cIdx;
                  return (
                    <CellTd
                      key={cell.m}
                      cell={cell}
                      metric={metric}
                      active={isActive}
                      onClick={() =>
                        toggleCell({ kind: 'row', rowIndex: rIdx, cellIndex: cIdx })
                      }
                    />
                  );
                })}
              </tr>
            ))}
            <tr className="border-t-2 border-line bg-sunken">
              <Td>
                <span className="font-medium text-ink">Weighted avg</span>
              </Td>
              <Td />
              {table.weighted.map((cell, cIdx) => {
                const isActive =
                  selected?.kind === 'weighted' && selected.cellIndex === cIdx;
                return (
                  <CellTd
                    key={cell.m}
                    cell={cell}
                    metric={metric}
                    active={isActive}
                    bold
                    onClick={() =>
                      toggleCell({ kind: 'weighted', cellIndex: cIdx })
                    }
                  />
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CellTd({
  cell,
  metric,
  active,
  bold,
  onClick,
}: {
  cell: CohortCell;
  metric: HeatmapMetric;
  active: boolean;
  bold?: boolean;
  onClick: () => void;
}) {
  const hasValue = cell.observable && cell.pct != null;
  const label = hasValue ? `${Math.round(cell.pct as number)}%${cell.partial ? '*' : ''}` : '—';
  const style = cellStyle(cell.pct, cell.observable, metric);
  const borderStyle: React.CSSProperties = cell.partial
    ? { outline: '1px dashed rgba(0,0,0,0.35)', outlineOffset: '-3px' }
    : {};
  const activeStyle: React.CSSProperties = active
    ? { boxShadow: 'inset 0 0 0 2px var(--ink, #000)' }
    : {};
  return (
    <td
      className="px-12 py-8 text-center align-middle cursor-pointer hover:brightness-95 focus:brightness-95 focus:outline-none"
      style={{ ...style, ...borderStyle, ...activeStyle }}
      title={cellTitle(cell, metric)}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <span className={bold ? 'font-medium' : undefined}>{label}</span>
    </td>
  );
}

function CellDetailPanel({
  table,
  row,
  cell,
  metric,
  onClose,
}: {
  table: ProductCohortTable;
  row: CohortRow | null;
  cell: CohortCell;
  metric: HeatmapMetric;
  onClose: () => void;
}) {
  const isCancellation = metric === 'cancellation';
  const colLabel = isCancellation ? 'W' : 'M';
  const verb = isCancellation ? 'cancelled' : 'retained';
  const noun = isCancellation ? 'cancellation' : 'retention';

  const cohortN = row?.n ?? table.n_total;
  const count = cell.retained ?? 0;
  // Use the eligible denominator (only subs whose own window has closed).
  // For fully-observable cells this equals cohortN; for partial cells it's
  // smaller — which matches the displayed percentage.
  const denom = cell.eligible ?? cohortN;
  const pending = Math.max(0, cohortN - denom);
  const remainder = Math.max(0, denom - count);
  const pctDisplay = cell.pct != null ? `${Math.round(cell.pct)}%` : '—';

  const label = table.product_label;
  const cohortLink = row ? buildCohortLink(label, row) : null;
  const matchedLink = row
    ? buildMatchedLink(label, row, cell, metric)
    : null;
  // Inverse of "matched": for retention, the subs that didn't clear the M_N
  // bar (paid_renewals_max = m - 1). For cancellation we already show the
  // cancelled set, so churned = retained complement, expressed as
  // paid_renewals_max = m - 1 against this cohort window. Only show when the
  // bar is meaningful (m >= 1) and at least one sub didn't retain.
  const churnedLink =
    row && metric === 'retention' && cell.m >= 1 && remainder > 0
      ? buildChurnedLink(label, row, cell)
      : null;

  const windowDates = `${format(parseISO(cell.window_start), 'MMM d, yyyy')} → ${format(
    parseISO(cell.window_end),
    'MMM d, yyyy'
  )}`;

  return (
    <div className="p-16 border-b border-line bg-sunken flex flex-col gap-12">
      <div className="flex items-start gap-12">
        <div className="flex-1 flex flex-col gap-4">
          <div className="type-label-sm text-ink-muted">
            {row
              ? `Cohort ${format(parseISO(row.cohort_start), 'MMM d')} → ${format(
                  parseISO(row.cohort_end),
                  'MMM d'
                )}`
              : 'Weighted average'}{' '}
            · {colLabel}
            {cell.m}
          </div>
          <div className="type-heading-sm">
            {pctDisplay} {verb}
            {cell.partial && (
              <span className="type-label-sm text-ink-muted font-normal">
                {' '}
                · partial (window still closing)
              </span>
            )}
          </div>
          <div className="type-label-sm text-ink-muted">
            {count.toLocaleString()} of {denom.toLocaleString()} eligible{' '}
            {isCancellation ? 'subs clicked cancel' : 'subs retained'} ·{' '}
            {remainder.toLocaleString()}{' '}
            {isCancellation ? 'did not cancel in this window' : 'did not renew'}
            {pending > 0 ? (
              <>
                {' '}
                · {pending.toLocaleString()} still pending ({colLabel}
                {cell.m} not yet elapsed)
              </>
            ) : null}
          </div>
          <div className="type-label-sm text-ink-muted">Window: {windowDates}</div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-4 rounded hover:bg-raised text-ink-muted hover:text-ink"
          aria-label="Close"
        >
          <X size={16} strokeWidth={1.5} />
        </button>
      </div>
      {row ? (
        <div className="flex flex-wrap gap-8">
          {cohortLink && (
            <a
              href={cohortLink}
              target="_blank"
              rel="noopener noreferrer"
              className="px-12 py-6 border border-line rounded type-label-sm bg-raised hover:bg-sunken text-ink"
            >
              View full cohort ({cohortN.toLocaleString()})
            </a>
          )}
          {matchedLink && count > 0 && (
            <a
              href={matchedLink}
              target="_blank"
              rel="noopener noreferrer"
              className="px-12 py-6 border border-line rounded type-label-sm bg-ink text-ink-onAccent hover:brightness-110"
            >
              View {verb} {colLabel}
              {cell.m} ({count.toLocaleString()})
            </a>
          )}
          {churnedLink && (
            <a
              href={churnedLink}
              target="_blank"
              rel="noopener noreferrer"
              className="px-12 py-6 border border-line rounded type-label-sm bg-raised hover:bg-sunken text-ink"
            >
              View churned {colLabel}
              {cell.m} ({remainder.toLocaleString()})
            </a>
          )}
        </div>
      ) : (
        <div className="type-label-sm text-ink-muted">
          Drill-down only available from individual cohort rows, not the weighted
          aggregate. Click a specific week above to view its {noun} cohort.
        </div>
      )}
    </div>
  );
}

// Match cohort.py's [cohort_start, cohort_end) UTC half-open Monday-to-Monday
// window. Backend uses inclusive `<=`, so encode the upper bound as 1ms
// before cohort_end. Sending plain "YYYY-MM-DD" instead would let the
// SubscriptionsScreen re-interpret in the viewer's local timezone, which
// shifts the boundary 7h east on PT and pulls the next cohort's signups
// into this cohort's drill-down list.
function cohortDateRange(row: CohortRow): { after: string; before: string } {
  const after = `${row.cohort_start}T00:00:00.000Z`;
  const before = new Date(
    new Date(`${row.cohort_end}T00:00:00.000Z`).getTime() - 1
  ).toISOString();
  return { after, before };
}

function buildCohortLink(productLabel: string, row: CohortRow): string {
  const params = new URLSearchParams();
  params.set('product_label', productLabel);
  const { after, before } = cohortDateRange(row);
  params.set('created_after', after);
  params.set('created_before', before);
  return `/admin/subscriptions?${params.toString()}`;
}

function buildMatchedLink(
  productLabel: string,
  row: CohortRow,
  cell: CohortCell,
  metric: HeatmapMetric
): string {
  const params = new URLSearchParams();
  params.set('product_label', productLabel);
  const { after, before } = cohortDateRange(row);
  params.set('created_after', after);
  params.set('created_before', before);
  if (metric === 'cancellation') {
    params.set('cancel_week', String(cell.m));
    params.set('status', 'cancelled');
  } else {
    // "retained at M_N" = >=N paid subscription_cycle renewals
    if (cell.m > 0) params.set('paid_renewals_min', String(cell.m));
  }
  return `/admin/subscriptions?${params.toString()}`;
}

// Inverse of buildMatchedLink for retention: the cohort subs that didn't
// reach the M_N bar — paid_renewals strictly less than the bar count.
function buildChurnedLink(
  productLabel: string,
  row: CohortRow,
  cell: CohortCell
): string {
  const params = new URLSearchParams();
  params.set('product_label', productLabel);
  const { after, before } = cohortDateRange(row);
  params.set('created_after', after);
  params.set('created_before', before);
  params.set('paid_renewals_max', String(cell.m - 1));
  return `/admin/subscriptions?${params.toString()}`;
}

function Th({
  children,
  align,
}: {
  children: React.ReactNode;
  align?: 'right' | 'center';
}) {
  return (
    <th
      className={`px-12 py-8 font-medium text-ink-muted ${
        align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : ''
      }`}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align,
  style,
  title,
}: {
  children?: React.ReactNode;
  align?: 'right' | 'center';
  style?: React.CSSProperties;
  title?: string;
}) {
  return (
    <td
      className={`px-12 py-8 ${
        align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : ''
      }`}
      style={style}
      title={title}
    >
      {children}
    </td>
  );
}

function cellStyle(
  pct: number | null,
  observable: boolean,
  metric: HeatmapMetric
): React.CSSProperties {
  if (!observable || pct == null) {
    return { color: 'var(--ink-muted, #999)' };
  }
  const intensity = Math.max(0, Math.min(100, pct)) / 100;
  if (metric === 'cancellation') {
    // Cancellation: red gradient. Most W_N cells are 0-15% in practice, so
    // scale up — 15% reads as strongly red instead of barely tinted.
    const scaled = Math.min(1, intensity * 4);
    const alpha = 0.08 + scaled * 0.6;
    return {
      backgroundColor: `rgba(200, 40, 40, ${alpha})`,
      color: scaled > 0.55 ? '#fff' : undefined,
    };
  }
  // Retention: dark ink gradient.
  const alpha = 0.08 + intensity * 0.5;
  return {
    backgroundColor: `rgba(0, 0, 0, ${alpha})`,
    color: intensity > 0.55 ? '#fff' : undefined,
  };
}

function cellTitle(
  cell: { window_start: string; window_end: string; retained: number | null; partial: boolean },
  metric: HeatmapMetric
): string {
  const start = format(parseISO(cell.window_start), 'MMM d, yyyy');
  const end = format(parseISO(cell.window_end), 'MMM d, yyyy');
  const noun = metric === 'cancellation' ? 'cancelled' : 'retained';
  const parts = [`${start} → ${end}`];
  if (cell.retained != null) parts.push(`${cell.retained} ${noun}`);
  if (cell.partial) parts.push('partial — window still closing');
  return parts.join(' · ');
}

/**
 * Build the subtitle line(s) under the product title. Shows experiment
 * context (for clones) and pricing (intro-mode shows phase 1 vs renewal
 * separately; flat pricing shows a single $/mo). Empty array = no subtitle.
 */
function buildSubtitles(table: ProductCohortTable): string[] {
  const lines: string[] = [];
  const md = table.metadata;
  if (md?.experiment_key) {
    const parts = [md.experiment_key];
    if (md.experiment_variant) parts.push(md.experiment_variant);
    if (md.experiment_frequency) parts.push(md.experiment_frequency);
    lines.push(`Experiment: ${parts.join(' · ')}`);
  }
  if (md?.first_phase_price_cents != null) {
    const first = formatCents(md.first_phase_price_cents);
    if (md.renewal_phase_price_cents != null) {
      const renewal = formatCents(md.renewal_phase_price_cents);
      lines.push(`${first} first month, then ${renewal}/mo`);
    } else {
      lines.push(`${first}/mo`);
    }
  }
  return lines;
}

function formatCents(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(cents / 100);
}
